import React, { FunctionComponent, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../../services/ssoRedirectService/oktaTokenStorage/tokenStorage';
import { OKTA_TOKEN_STORAGE_CHANGED_EVENT } from '../../services/ssoRedirectService/oktaTokenStorage/tokenStorage.constants';

export type InspectSsoTokenButtonProps = {};

export const InspectSsoTokenButton: FunctionComponent<PropsWithChildren<InspectSsoTokenButtonProps>> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string>('');

  const refreshAccessToken = useCallback(() => {
    const token = getAccessToken();
    setAccessToken(token?.accessToken ?? '');
  }, []);

  useEffect(() => {
    refreshAccessToken();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAccessToken();
      }
    };

    window.addEventListener('focus', refreshAccessToken);
    window.addEventListener(OKTA_TOKEN_STORAGE_CHANGED_EVENT, refreshAccessToken);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshAccessToken);
      window.removeEventListener(OKTA_TOKEN_STORAGE_CHANGED_EVENT, refreshAccessToken);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAccessToken]);

  const handleInspectJWT = useCallback(() => {
    const token = getAccessToken();
    const currentToken = token?.accessToken ?? accessToken;
    setAccessToken(currentToken);
    if (!currentToken) return;

    const jwtIoUrl = `https://jwt.io/?token=${encodeURIComponent(currentToken)}`;
    window.open(jwtIoUrl, '_blank');
  }, [accessToken]);

  const hasAccessToken = useMemo(() => Boolean(accessToken), [accessToken]);
  const className = useMemo(() => `inspect-sso ${hasAccessToken ? 'online' : 'offline'}`, [hasAccessToken]);

  const statusClass = useMemo(
    () =>
      `status before:content-[''] before:inline-block before:w-2 before:h-2 before:p-0 before:mt-0 before:mr-[0.2rem] before:mb-0 before:ml-0 before:rounded-full ${
        hasAccessToken ? 'before:bg-mint' : 'before:bg-cherry'
      }`,
    [hasAccessToken],
  );

  return (
    <button className={className} onClick={handleInspectJWT}>
      <span className={statusClass}></span>
      {children}
    </button>
  );
};
